/**
 * 閲覧ページ /d/:token（#225、docs/91 §3.3）：メールを受け取った人が、ログインせずに書類を表示・ダウンロードする1枚。
 *   アプリの上の帯とメニューは出さない（ログイン前の画面と同じくライト）。PC もスマホも縦1列・押す所 56px
 *   頭：送った会社名（大きく）・件名・送信日・公開期限 → 書類の一覧（書類名・番号・ページ数・大きさ）と書類ごとの「表示」（このページの中にページ画像＝pdf.js。PDF だけ）・
 *   「ダウンロード」、2つ以上なら「一括ダウンロード」（ZIP）→ 下に製品のロゴを小さく
 *   公開を終えた（無効にした・公開期限が過ぎた＝410）：止めの画面「この書類は公開を終了しました」と送った会社名。無い鍵（404）は「ページが見つかりません」（会社名も出さない）
 *   取得の記録は API が付ける（開いた・表示・ダウンロード）。リファラーを送らない・検索に出さない（Caddy も /d/* に Referrer-Policy・X-Robots-Tag）
 */
import { ACCEPTANCE_PUBLIC_MSG as AM, MAIL_MSG as M, STOP_MSG, sizeText, type DeliveryPublicPage } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { StopScreen } from "../../components/StopScreen.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { renderPdf, type PageImg } from "../../lib/pdfPages.js";
import { PRELOGIN_PRODUCT, productInfo } from "../../lib/product.js";

class DeliveryApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: { title?: string; code?: string; company?: string; state?: string },
  ) {
    super(body.title ?? "いま処理できませんでした");
  }
}

/** 公開の API（控えを使わない・リファラーを送らない。Cookie は同じサイトなので送る＝送った会社でログイン中かを API が見る） */
async function dget(path: string): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/d/${path}`, { credentials: "same-origin", referrerPolicy: "no-referrer", cache: "no-store" });
  } catch {
    throw new DeliveryApiError(0, { title: "つながりませんでした。電波の状態を確かめて、もう一度お試しください" });
  }
  if (!res.ok) {
    let body: DeliveryApiError["body"] = {};
    try {
      body = (await res.json()) as DeliveryApiError["body"];
    } catch {
      /* JSON でない答え */
    }
    throw new DeliveryApiError(res.status, body);
  }
  return res;
}

/** このページではリファラーを送らない・検索に出さない（開発の Vite 用に meta も） */
function useNoReferrer(title: string) {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "referrer";
    m.content = "no-referrer";
    const r = document.createElement("meta");
    r.name = "robots";
    r.content = "noindex, nofollow";
    document.head.append(m, r);
    const prev = document.title;
    document.title = title;
    return () => {
      m.remove();
      r.remove();
      document.title = prev;
    };
  }, [title]);
}

const ymdSlash = (iso: string) => {
  const t = new Date(Date.parse(iso) + 9 * 3600 * 1000);
  return `${t.getUTCFullYear()}/${String(t.getUTCMonth() + 1).padStart(2, "0")}/${String(t.getUTCDate()).padStart(2, "0")}`;
};

export function DeliveryViewPage() {
  const { token = "" } = useParams();
  useNoReferrer(M.pageTitle);
  const P = productInfo(PRELOGIN_PRODUCT);
  const q = useQuery({
    queryKey: ["delivery-page", token],
    queryFn: async () => (await (await dget(encodeURIComponent(token))).json()) as DeliveryPublicPage,
    retry: false,
    staleTime: Infinity,
  });
  const [viewing, setViewing] = useState<{ no: number; label: string; pages: PageImg[] | null; error?: string } | null>(null);
  const openView = async (no: number, label: string) => {
    setViewing({ no, label, pages: null });
    try {
      const blob = await (await dget(`${encodeURIComponent(token)}/files/${no}`)).blob();
      const got: PageImg[] = [];
      await renderPdf(await blob.arrayBuffer(), (p, i) => {
        got[i - 1] = p;
      });
      setViewing({ no, label, pages: got });
    } catch (e) {
      setViewing({ no, label, pages: [], error: e instanceof Error ? e.message : String(e) });
    }
  };

  if (q.isError) {
    const e = q.error instanceof DeliveryApiError ? q.error : null;
    if (e?.status === 410) return <StopScreen art="lock" title={STOP_MSG.deliveryGone.title} body={STOP_MSG.deliveryGone.body(e.body.company ?? "")} testId="delivery-gone" />;
    if (e?.status === 404) return <StopScreen art="question" title={STOP_MSG.deliveryNotFound.title} body={STOP_MSG.deliveryNotFound.body} testId="delivery-notfound" />;
    return (
      <div className="dview" data-testid="delivery-view">
        <ErrorText>{q.error instanceof Error ? q.error.message : String(q.error)}</ErrorText>
      </div>
    );
  }
  const d = q.data;
  return (
    <div className="dview" data-testid="delivery-view">
      {!d ? (
        <p className="hint">{M.pageLoading}</p>
      ) : (
        <>
          <header className="dview-head">
            <p className="dview-kicker">{M.pageTitle}</p>
            <h1 className="dview-company" data-testid="delivery-company">
              {d.company}
            </h1>
            <p className="dview-subject" data-testid="delivery-subject">
              {d.subject}
            </p>
            <dl className="dview-meta">
              <dt>{M.pageSentAt}</dt>
              <dd className="num">{ymdSlash(d.sentAt)}</dd>
              <dt>{M.pageExpires}</dt>
              <dd className="num" data-testid="delivery-expires">
                {M.expiresUntil.replace("{d}", ymdSlash(d.expiresAt))}
              </dd>
            </dl>
          </header>
          <ul className="dview-files" data-testid="delivery-files">
            {d.files.map((f) => (
              <li key={f.no} className="dview-file" data-testid="delivery-file">
                <div className="dview-fname">
                  <b>{f.docNo ? `${f.label}　${f.docNo}` : f.label}</b>
                  <span className="hint num">{[f.pages ? M.pagePages.replace("{n}", String(f.pages)) : null, sizeText(f.bytes)].filter(Boolean).join("・")}</span>
                </div>
                <div className="dview-acts">
                  {f.contentType === "application/pdf" ? (
                    <button type="button" className="btn" onClick={() => void openView(f.no, f.label)} data-testid="delivery-view-file">
                      {M.pageView}
                    </button>
                  ) : null}
                  <a className="btn primary" href={`/api/v1/d/${encodeURIComponent(token)}/files/${f.no}?dl=1`} download={f.fileName} rel="noreferrer" data-testid="delivery-download">
                    {M.pageDownload}
                  </a>
                </div>
              </li>
            ))}
          </ul>
          {/* 同封した{deliveryNote}・{acceptance}の「受領」「検収」（#266 1b）：受領・検収のページ /a/<鍵> へ（押す前に内容を確かめる1枚） */}
          {d.acks.length ? (
            <ul className="dview-files" data-testid="delivery-acks">
              {d.acks.map((a) => {
                const word = a.kind === "accept" ? AM.accept : AM.receive;
                return (
                  <li key={a.path} className="dview-file" data-testid="delivery-ack">
                    <div className="dview-fname">
                      <b>{a.docNo ? `${a.label}　${a.docNo}` : a.label}</b>
                      {a.done ? <span className="hint" data-testid="delivery-ack-done">{`${word}済`}</span> : null}
                    </div>
                    <div className="dview-acts">
                      <a className={a.done ? "btn" : "btn primary"} href={a.path} rel="noreferrer" data-testid="delivery-ack-open">
                        {a.done ? "表示" : word}
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
          {d.files.length > 1 ? (
            <p className="dview-zip">
              <a className="btn" href={`/api/v1/d/${encodeURIComponent(token)}/zip`} rel="noreferrer" data-testid="delivery-zip">
                {M.pageZip}
              </a>
            </p>
          ) : null}
          <footer className="dview-foot">
            <img src={P.assets.logoHeader} alt={P.name} width={96} height={30} />
            <span>{M.pagePowered.replace("{company}", d.company).replace("{product}", P.name)}</span>
          </footer>
        </>
      )}
      {viewing ? (
        <div className="dview-viewer" role="dialog" aria-modal="true" aria-label={viewing.label} onKeyDown={(e) => e.key === "Escape" && setViewing(null)} data-testid="delivery-viewer">
          <div className="dview-vbar">
            <b>{viewing.label}</b>
            <span className="sp" />
            <button type="button" className="btn" autoFocus onClick={() => setViewing(null)} data-testid="delivery-viewer-close">
              {M.pageClose}
            </button>
          </div>
          <div className="dview-vpages">
            {viewing.error ? <ErrorText>{viewing.error}</ErrorText> : null}
            {viewing.pages === null ? <p className="hint">{M.pageLoading}</p> : null}
            {(viewing.pages ?? []).map((p, i) => (
              <div className="paperpage" key={i}>
                <img src={p.url} alt={`${viewing.label} ${i + 1}ページ目`} width={p.w} height={p.h} />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
